import { nextTick, onBeforeUnmount, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const TEXT_SELECTOR = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'li',
  'dt',
  'dd',
  'blockquote',
  'figcaption',
  'caption',
  'th',
  'td'
].join(',')

const SKIP_SELECTOR = [
  'nav',
  'footer',
  'button',
  'input',
  'textarea',
  'select',
  'label',
  'a[href]',
  '[role="navigation"]',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="tablist"]',
  '[data-text-reveal="off"]',
  '[aria-hidden="true"]',
  'script',
  'style',
  'noscript',
  'svg',
  'canvas',
  'video',
  '.pixso-home__blur-word',
  '.pixso-home__blur-char',
  '.pixso-nav',
  '.footer',
  '.pixso-button',
  '.spec-table__buttons',
  '.spec-table__button',
  '.banner',
  '[class*="banner"]',
  '[class*="__hero"]',
  '[class*="-hero"]',
  '[class*="hero-"]',
  '[class*="__button"]',
  '[class*="-button"]',
  '[class*="__tab"]',
  '[class*="-tab"]'
].join(',')

const REVEAL_CLASS = 'global-text-reveal'
const VISIBLE_CLASS = 'global-text-reveal--visible'
const WORD_CLASS = 'global-text-reveal__word'
const CHAR_CLASS = 'global-text-reveal__char'
const NOWRAP_CLASS = 'global-text-reveal__nowrap'
const PROCESSED_ATTR = 'data-global-text-reveal'
const INLINE_REVEAL_SELECTOR = '[data-text-reveal="inline"]'
const CLOSING_PUNCTUATION_PATTERN = /^[,.;:!?\)\]\}\u3001\u3002\u3009\u300B\u300D\u300F\u3011\u3015\u3017\u3019\u301B\u2019\u201D\u2026\uFF01\uFF02\uFF07\uFF09\uFF0C\uFF0E\uFF1A\uFF1B\uFF1F\uFF3D\uFF5D]$/u

const INLINE_TEXT_SELECTOR = 'span,strong,em,small,b,i'
const CHILD_SKIP_SELECTOR = [
  'img',
  'picture',
  'svg',
  'canvas',
  'video',
  'audio',
  'input',
  'textarea',
  'select',
  'iframe',
  'a[href]',
  '[aria-hidden="true"]',
  '[data-text-reveal="off"]',
  `.${CHAR_CLASS}`
].join(',')

export function useGlobalTextReveal() {
  const route = useRoute()
  const { locale } = useI18n({ useScope: 'global' })
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  let intersectionObserver = null
  let mutationObserver = null
  let scheduledFrame = 0

  const splitGraphemes = (text) => {
    if (typeof Intl !== 'undefined' && Intl.Segmenter) {
      const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' })
      return Array.from(segmenter.segment(text), ({ segment }) => segment)
    }

    return Array.from(text)
  }

  const mergeNumericCjkSegments = (segments) => {
    const merged = []
    const cjkUnitPattern = /^[\u00B0%\u2032\u2033\u2103\u2109A-Za-z\u4E00-\u9FFF]+$/

    for (let index = 0; index < segments.length; index += 1) {
      const current = segments[index]

      if (!current || /^\s+$/.test(current)) {
        merged.push(current)
        continue
      }

      const next = segments[index + 1]
      const nextNext = segments[index + 2]

      if (
        /^\d+(?:[./xX×~-]\d+)*$/.test(current) &&
        /^\s+$/.test(next || '') &&
        cjkUnitPattern.test(nextNext || '')
      ) {
        merged.push(`${current}${next}${nextNext}`)
        index += 2
        continue
      }

      if (
        /^\d+(?:\.\d+)?$/.test(current) &&
        cjkUnitPattern.test(next || '')
      ) {
        merged.push(`${current}${next}`)
        index += 1
        continue
      }

      merged.push(current)
    }

    return merged.filter(Boolean)
  }

  const groupTrailingPunctuation = (characters) => {
    return characters.reduce((groups, character, index) => {
      const previousGroup = groups[groups.length - 1]
      const nextCharacter = characters[index + 1]

      if (
        previousGroup &&
        (CLOSING_PUNCTUATION_PATTERN.test(character) ||
          (/^\s+$/.test(character) && CLOSING_PUNCTUATION_PATTERN.test(nextCharacter || '')))
      ) {
        previousGroup.push(character)
        return groups
      }

      groups.push([character])
      return groups
    }, [])
  }

  const extractLeadingClosingPunctuation = (text) => {
    const characters = splitGraphemes(text || '')
    const leadingCharacters = []
    let index = 0

    while (/^\s+$/.test(characters[index] || '')) {
      leadingCharacters.push(characters[index])
      index += 1
    }

    const punctuationStartIndex = index

    while (CLOSING_PUNCTUATION_PATTERN.test(characters[index] || '')) {
      leadingCharacters.push(characters[index])
      index += 1
    }

    if (index === punctuationStartIndex) {
      return null
    }

    return {
      leadingText: leadingCharacters.join(''),
      remainingText: characters.slice(index).join('')
    }
  }

  const normalizeLeadingPunctuationTextNodes = (textNodes) => {
    let previousTextNode = null

    textNodes.forEach((textNode) => {
      const leading = previousTextNode
        ? extractLeadingClosingPunctuation(textNode.textContent || '')
        : null

      if (leading) {
        previousTextNode.textContent = `${previousTextNode.textContent || ''}${leading.leadingText}`
        textNode.textContent = leading.remainingText
      }

      if (textNode.textContent?.trim()) {
        previousTextNode = textNode
      }
    })

    return textNodes.filter((textNode) => textNode.textContent?.trim())
  }

  const isClosingPunctuationSegment = (segment) => {
    return splitGraphemes(segment).every((char) => CLOSING_PUNCTUATION_PATTERN.test(char))
  }

  const mergeTrailingPunctuationSegments = (segments) => {
    const merged = []

    for (let index = 0; index < segments.length; index += 1) {
      const current = segments[index]
      const next = segments[index + 1]

      if (
        merged.length &&
        /^\s+$/.test(current || '') &&
        next &&
        isClosingPunctuationSegment(next)
      ) {
        merged[merged.length - 1] += `${current}${next}`
        index += 1
        continue
      }

      if (merged.length && current && isClosingPunctuationSegment(current)) {
        merged[merged.length - 1] += current
        continue
      }

      merged.push(current)
    }

    return merged
  }

  const createAnimatedChar = (char, charIndex) => {
    const charElement = document.createElement('span')
    charElement.className = CHAR_CLASS
    charElement.style.setProperty('--global-text-reveal-char-index', String(charIndex))
    charElement.textContent = char
    return charElement
  }

  const hasVisibleText = (element) => {
    return Boolean(element.textContent && element.textContent.trim())
  }

  const hasAuthorChildElements = (element) => {
    return Array.from(element.children).some((child) => {
      return !child.classList.contains(WORD_CLASS) && !child.classList.contains(CHAR_CLASS)
    })
  }

  const shouldSkipElement = (element) => {
    if (!hasVisibleText(element) || element.closest(SKIP_SELECTOR)) {
      return true
    }

    if (!element.hasAttribute(PROCESSED_ATTR) && !element.matches(INLINE_REVEAL_SELECTOR) && hasAuthorChildElements(element)) {
      return true
    }

    const parentTextElement = element.parentElement?.closest(TEXT_SELECTOR)
    return Boolean(parentTextElement)
  }

  const shouldSkipTextNode = (textNode, rootElement) => {
    const parentElement = textNode.parentElement

    if (!parentElement || parentElement === rootElement) {
      return false
    }

    if (parentElement.closest(CHILD_SKIP_SELECTOR)) {
      return true
    }

    return !parentElement.matches(INLINE_TEXT_SELECTOR)
  }

  const createAnimatedText = (text, startIndex) => {
    const fragment = document.createDocumentFragment()
    let charIndex = startIndex
    const segments = mergeTrailingPunctuationSegments(
      mergeNumericCjkSegments(text.match(/\s+|[^\s]+/g) || [])
    )

    segments.forEach((segment) => {
      if (/^\s+$/.test(segment)) {
        fragment.appendChild(document.createTextNode(segment))
        return
      }

      const word = document.createElement('span')
      word.className = WORD_CLASS

      groupTrailingPunctuation(splitGraphemes(segment)).forEach((characters) => {
        const target = characters.length > 1 ? document.createElement('span') : word

        if (target !== word) {
          target.className = NOWRAP_CLASS
        }

        characters.forEach((char) => {
          target.appendChild(createAnimatedChar(char, charIndex))
          charIndex += 1
        })

        if (target !== word) {
          word.appendChild(target)
        }
      })

      fragment.appendChild(word)
    })

    return { fragment, nextIndex: charIndex }
  }

  const splitTextElement = (element) => {
    if (element.hasAttribute(PROCESSED_ATTR)) {
      return
    }

    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode: (textNode) => {
        if (!textNode.textContent?.trim() || shouldSkipTextNode(textNode, element)) {
          return NodeFilter.FILTER_REJECT
        }

        return NodeFilter.FILTER_ACCEPT
      }
    })
    const textNodes = []

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode)
    }

    if (textNodes.length === 0) {
      return
    }

    let charIndex = 0
    normalizeLeadingPunctuationTextNodes(textNodes).forEach((textNode) => {
      const { fragment, nextIndex } = createAnimatedText(textNode.textContent, charIndex)
      textNode.parentNode.replaceChild(fragment, textNode)
      charIndex = nextIndex
    })

    element.setAttribute(PROCESSED_ATTR, 'true')
  }

  const revealImmediately = (element) => {
    element.classList.add(REVEAL_CLASS, VISIBLE_CLASS)
  }

  const observeElement = (element) => {
    if (reducedMotionQuery.matches) {
      revealImmediately(element)
      return
    }

    splitTextElement(element)
    element.classList.add(REVEAL_CLASS)

    if (element.classList.contains(VISIBLE_CLASS)) {
      return
    }

    intersectionObserver?.observe(element)
  }

  const scanTextElements = () => {
    scheduledFrame = 0
    const appRoot = document.getElementById('app')

    if (!appRoot) {
      return
    }

    const candidates = Array.from(appRoot.querySelectorAll(TEXT_SELECTOR)).filter((element) => !shouldSkipElement(element))
    candidates.forEach(observeElement)
  }

  const scheduleScan = () => {
    if (scheduledFrame) {
      return
    }

    scheduledFrame = window.requestAnimationFrame(scanTextElements)
  }

  const createIntersectionObserver = () => {
    intersectionObserver?.disconnect()
    intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return
        }

        entry.target.classList.add(VISIBLE_CLASS)
        intersectionObserver?.unobserve(entry.target)
      })
    }, {
      rootMargin: '50px',
      threshold: [0, 0.01]
    })
  }

  const resetProcessedElements = () => {
    const appRoot = document.getElementById('app')

    if (!appRoot) {
      return
    }

    appRoot.querySelectorAll(`[${PROCESSED_ATTR}]`).forEach((element) => {
      element.querySelectorAll(`.${WORD_CLASS}`).forEach((word) => {
        word.replaceWith(document.createTextNode(word.textContent || ''))
      })
      element.querySelectorAll(`.${CHAR_CLASS}`).forEach((char) => {
        char.replaceWith(document.createTextNode(char.textContent || ''))
      })
      element.normalize()
      element.removeAttribute(PROCESSED_ATTR)
      element.classList.remove(REVEAL_CLASS, VISIBLE_CLASS)
    })
  }

  onMounted(() => {
    createIntersectionObserver()

    mutationObserver = new MutationObserver(scheduleScan)
    mutationObserver.observe(document.getElementById('app'), {
      childList: true,
      subtree: true
    })

    scheduleScan()
  })

  watch(
    () => route.fullPath,
    async () => {
      intersectionObserver?.disconnect()
      createIntersectionObserver()
      await nextTick()
      scheduleScan()
    }
  )

  watch(
    () => locale.value,
    async () => {
      intersectionObserver?.disconnect()
      createIntersectionObserver()
      resetProcessedElements()
      await nextTick()
      scheduleScan()
    }
  )

  onBeforeUnmount(() => {
    if (scheduledFrame) {
      window.cancelAnimationFrame(scheduledFrame)
    }

    intersectionObserver?.disconnect()
    mutationObserver?.disconnect()
  })
}
